'use client';

import type {Resource} from 'i18next';
import type React from 'react';
import {useEffect, useRef} from 'react';
import {I18nextProvider} from 'react-i18next';
import {initI18next} from '@/i18n/client';

interface TranslationsProviderProps {
  children: React.ReactNode;
  locale: string;
  resources?: Resource;
}

export function TranslationsProvider({
  children,
  locale,
  resources,
}: TranslationsProviderProps): React.JSX.Element {
  const i18nRef = useRef<ReturnType<typeof initI18next> | null>(null);

  if (!i18nRef.current) {
    i18nRef.current = initI18next(locale, resources);
  }

  useEffect(() => {
    const i18n = i18nRef.current;
    if (!i18n) return;

    const localeResources = resources?.[locale];
    if (localeResources) {
      for (const ns of Object.keys(localeResources)) {
        if (!i18n.hasResourceBundle(locale, ns)) {
          i18n.addResourceBundle(locale, ns, localeResources[ns], true, true);
        }
      }
    }

    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, resources]);

  return <I18nextProvider i18n={i18nRef.current}>{children}</I18nextProvider>;
}
